"use client";

import React, { useRef, useEffect, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import ChatInput from "../ChatInput/ChatInput";
import ChatMessage from "../ChatMessage/ChatMessage";
import ChatHeader from "../ChatHeader/ChatHeader";
import useMessaging from "../../hooks/useMessaging.js";
import useChatRestriction from "../../hooks/useChatRestriction.js";
import { useMessaging as useMessagingCtx } from "../../context/MessagingContext.jsx";
import "./ChatWindow.css";

function toMsgTs(m) {
  if (!m) return 0;
  if (m.createdAt) {
    const d = Date.parse(m.createdAt);
    if (!Number.isNaN(d)) return d;
  }
  const n = Number(m.timestamp || m.time || m.sentAt || 0);
  if (n > 0) return n;
  if (m.updatedAt) {
    const d2 = Date.parse(m.updatedAt);
    if (!Number.isNaN(d2)) return d2;
  }
  return 0;
}

function TypingIndicator({ contact }) {
  const name = contact?.name || contact?.fullName || contact?.username || "?";
  const photo =
    contact?.avatarUrl ||
    contact?.photo ||
    contact?.image ||
    contact?.avatar ||
    contact?.meta?.avatarUrl ||
    null;
  const initial = name?.[0]?.toUpperCase() || "?";

  return (
    <div className="cw-typing-bubble">
      <div className="cw-typing-avatar">
        {photo ? (
          <img
            src={photo}
            alt={name}
            className="cw-typing-avatar-img"
            onError={(e) => {
              e.currentTarget.style.display = "none";
              e.currentTarget.nextSibling.style.display = "flex";
            }}
          />
        ) : null}
        <span
          className="cw-typing-avatar-fallback"
          style={{ display: photo ? "none" : "flex" }}
        >
          {initial}
        </span>
      </div>
      <div className="cw-typing-dots">
        <span className="cw-dot" />
        <span className="cw-dot" />
        <span className="cw-dot" />
      </div>
    </div>
  );
}

export default function ChatWindow({ selectedContact, meOverride, useSocket = true }) {
  const { t } = useTranslation();

  const { bumpContact, getPeerId, formattedUserId, setSharedMessages, isDoctor: isDoctorViewer } = useMessagingCtx();

  const peerId = getPeerId?.(selectedContact) || null;
  const activePeerId = peerId ? String(peerId).replace(/^DR_/i, "") : null;

  const scrollRef = useRef(null);

  const {
    status: restrictionStatus,
    busy: restrictionBusy,
    setRestriction,
    clearRestriction,
  } = useChatRestriction(peerId, { canManage: Boolean(isDoctorViewer) });

  const {
    me,
    messages = [],
    loading,
    error,
    sendText,
    uploadFile,
    emitTyping,
    typingFrom,
  } = useMessaging(peerId, {
    autoFetch: !!peerId,
    useSocket,
    meOverride,
    conversationId: selectedContact?.meta?.conversationId || null,
  });

  useEffect(() => {
    if (setSharedMessages) setSharedMessages(messages);
  }, [messages, setSharedMessages]);

  useEffect(() => {
    if (setSharedMessages) setSharedMessages([]);
  }, [peerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const isChatBlocked = restrictionStatus.blockedByMe || restrictionStatus.blockedByPeer;

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, typingFrom]);

  const lastBumpedRef = useRef({ msgId: null, ts: 0 });

  useEffect(() => {
    if (!peerId) return;
    if (!Array.isArray(messages) || messages.length === 0) return;
    const last = messages[messages.length - 1];
    const msgId = String(last?.id || last?._id || "");
    const ts = toMsgTs(last);
    if (!ts) return;
    if (msgId && lastBumpedRef.current.msgId === msgId) return;
    if (!msgId && ts === lastBumpedRef.current.ts) return;
    lastBumpedRef.current = { msgId: msgId || null, ts };
    bumpContact?.(String(peerId), Number(ts), "message");
  }, [peerId, messages, bumpContact]);

  const handleTyping = useCallback(
    (isTyping) => {
      if (!peerId || isChatBlocked) return;
      emitTyping(isTyping);
    },
    [peerId, emitTyping, isChatBlocked]
  );

  const isPeerTyping = Boolean(
    activePeerId &&
      typingFrom &&
      String(typingFrom) === activePeerId &&
      !isChatBlocked
  );

  const handleSendText = useCallback(
    (text) => {
      if (isChatBlocked) return;
      sendText(text);
    },
    [sendText, isChatBlocked]
  );

  const handleUploadFile = useCallback(
    (file) => {
      if (isChatBlocked) return;
      uploadFile(file);
    },
    [uploadFile, isChatBlocked]
  );

  if (!peerId) {
    return <div className="cw-empty">{t("chatWindow.selectContact")}</div>;
  }

  const confirmBlock = () => {
    if (window.confirm(t("chatWindow.confirmBlock"))) setRestriction("BLOCK");
  };

  return (
    <div className="chat-window">
      {/* ✅ FIX: passer selectedContact comme contactOverride pour que
          ChatHeader affiche le vrai nom même si le contexte est en retard */}
      <ChatHeader
        contactOverride={selectedContact}
        restriction={
          isDoctorViewer
            ? {
                status: restrictionStatus,
                busy: restrictionBusy,
                onRestrict: () => setRestriction("RESTRICT"),
                onBlock: confirmBlock,
                onClear: clearRestriction,
              }
            : null
        }
      />

      <div className="cw-messages" ref={scrollRef}>
        <div className="cw-messages__inner">
          {messages.map((msg) => (
            <ChatMessage key={msg.id || msg._id} msg={msg} me={me} />
          ))}

          {isPeerTyping && <TypingIndicator contact={selectedContact} />}

          {error && <div className="cw-error">{error}</div>}
        </div>
      </div>

      {restrictionStatus.restrictedByMe && !isChatBlocked && (
        <div className="cw-restricted-info">{t("chatWindow.restrictedByMeInfo")}</div>
      )}

      {!isChatBlocked ? (
        <ChatInput
          onSendMessage={handleSendText}
          onUploadFile={handleUploadFile}
          onTyping={handleTyping}
        />
      ) : (
        <div className="cw-restricted-notice">
          <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          <span>
            {restrictionStatus.blockedByMe
              ? t("chatWindow.blockedByMe")
              : t("chatWindow.blockedByPeer")}
          </span>
          {restrictionStatus.blockedByMe && isDoctorViewer && (
            <button
              type="button"
              className="cw-unblock-btn"
              disabled={restrictionBusy}
              onClick={clearRestriction}
            >
              {t("chatWindow.unblock")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}